<script lang="ts">
    import type { Fn, Question, QuestionOption } from "../global";
    import { createEventDispatcher } from 'svelte';
    import type ChallengePoint from "../Model/ChallengePoint";
    import Rotary from "./Rotary.svelte";
    import type { Readable } from "svelte/store";

    type OptionState = {
        option: QuestionOption;
        isAnswer: boolean;
    };

    export let challenge: ChallengePoint;

    const showFeedbackDelay = 1500;
    const dispatch = createEventDispatcher();

    let rotate: Fn;
    let question: Question;
    let feedbackText: string = '';
    let questionIndex: number;
    let questionNumber: number;
    let isAnswered: boolean;
    let optionStates: OptionState[];
    let showResume: boolean = false;
    let wins: number;
    let fails: number;
    let isSuccess: boolean;
    let isCorrect: boolean;
    let showFeedback: boolean;
    let isFaceA: Readable<boolean>;

    $: {
        challenge;
        resetChallenge();
    }

    $: if ($isFaceA) {
        nextFeedback();
    }

    function resetChallenge() {
        wins = 0;
        fails = 0;
        isSuccess = false;
        questionIndex = -1;
        questionNumber = 0;
        nextQuestion();
    }

    function nextQuestion() {
        questionIndex++;
        isAnswered = false;
        showFeedback = false;

        if (questionIndex >= challenge.questions.length) {
            //Se han terminado las preguntas
            enableResume();
            return;
        }

        questionNumber++;
        question = challenge.questions[questionIndex];
        optionStates = question.options.map(option => ({ option, isAnswer: false }));
        optionStates.sort(() => -0.5 + Math.random());
    }

    function nextFeedback() {
        feedbackText = question?.feedback.default || '';
    }

    function enableResume() {
        question = null;
        isSuccess = wins > fails;
        showResume = true;
    }

    function onOptionClick(index: number) {
        if (isAnswered) {
            return;
        }

        isAnswered = true;
        optionStates[index].isAnswer = true;

        if (optionStates[index].option.isCorrect) {
            isCorrect = true;
            wins++;
        } else {
            isCorrect = false;
            fails++;
        }

        setTimeout(spinPanel, showFeedbackDelay);   
    }

    function onContinue() {
        if (isSuccess) {
            dispatch('success');
        } else {
            dispatch('fail');
        }
    }

    function spinPanel() {
        rotate?.(-0.5);
    }

    function onNext() {
        nextQuestion();
        spinPanel();
    }

</script>

<style lang="scss">

    @use '../mixins';
    @use '../variables';

    .challenge {
        height: 100%;
        box-sizing: border-box;
        position: relative;
        perspective: 2000px;
    }

    .white-panel {
        background-color: #fff;
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
        border-radius: 24px;
    }

    .indicator {
        $size: 3.2rem;
        position: absolute;
        top: -30px;
        left: calc(50% - #{$size * 0.5});
        width: $size;
        height: $size;
        border-radius: 50%;
        display: flex;
        justify-content: center;
        align-items: center;
        color: #fff;
        font-family: var(--page-title-family) ;
		font-weight: var(--page-title-weight);
        font-size: 1em;
    }

    .question {
        height: 100%;
        display: flex;
        flex-direction: column;
        justify-content: space-evenly;
        padding: 1rem;
        box-sizing: border-box;

        .indicator {
            background-color: var(--color-secondary);
        }
    }

    .feedback {
        height: 100%;
        padding: 1rem;
        box-sizing: border-box;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        overflow: hidden;
        padding-top: 2rem;
        text-align: center;

        .win-icon {
            $size: 2rem;
            width: $size;
            height: $size;
        }

        .fail-icon {
            $size: 1.6rem;
            width: $size;
            height: $size;
        }

        &.feedback-win {
            color: var(--color-success);

            .indicator {
                background-color: var(--color-success);
            }
        }

        &.feedback-fail {
            color: var(--color-fail);

            .indicator {
                background-color: var(--color-fail);
            }
        }
    }
    .statement {
        display: flex;
        flex-direction: column;
        justify-content: center;
        text-align: center;
        font-size: 1.62rem;
        font-family: var(--page-body-family) ;
		font-weight: var(--page-title-weight);
        color: var(--color-secondary);
    }

    .options {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
    }

    .feedback-text {
        flex: 2 1 0;
        overflow: auto;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-direction: column;
    }

    .feedback-btn-container {
        flex: 1 1 0;
        display: flex;
        justify-content: center;
        flex-direction: column;
    }

    .next-btn,
    .continue-btn {
        @include mixins.big-btn;
    }

    .option {
        @include mixins.big-btn;
        background-color: var(--route-color, --color-primary);
        font-size: 1.375rem;

        &.is-correct {
            background-color: var(--color-success);
        }

        &.is-fail {
            background-color: var(--color-fail);
        }
    }

    .resume {
        height: 100%;
        display: flex;
        flex-direction: column;
        justify-content: flex-end;
    }

    .resume-box {
        position: relative;
        height: 70%;
        padding: 7rem 1rem 1rem;
        box-sizing: border-box;
        display: flex;
        flex-direction: column;
        justify-content: space-evenly;
        font-size: 1.6rem;
        text-align: center;
    }

    .manzana-final {
        position: absolute;
        top: -50%;
        left: 50%;
        transform: translateX(-50%);
        width: 75%;
        max-width: 300px;
    }

    .win-text {
        color: var(--color-success);
        margin-bottom: 2rem;
    }

    .fail-text {
        color: var(--color-fail);
        margin-bottom: 2rem;
    }

    @media (max-width: variables.$big-phone-width) {

        .statement {
            font-size: 1.5rem;
        }

        .option {
            font-size: 1.1rem;
        }

        .feedback {
            padding-top: 0.5rem;
        }

        .resume-box {
            padding-top: 4rem;
            font-size: 1.3rem;
        }

        .continue-btn {
            font-size: 1.2rem;
        }

    }

    .face {
        width: 100%;
        height: 100%;
    }

</style>

<div class="challenge">

    <Rotary bind:rotate bind:isFaceA>
        <div class="face" slot="face-a">
            {#if question}
            <div class="question white-panel">

                <div class="indicator" class:feedback-mode={showFeedback}>
                    <div class="step">{questionNumber} / {challenge.questions.length}</div>
                </div>
    
                <div class="statement">{question.statement}</div>
                <div class="options">
                    {#each optionStates as optionState, index}
                    <button
                        type="button"
                        class="option"
                        class:is-correct={isAnswered && optionState.option.isCorrect}
                        class:is-fail={optionState.isAnswer && !optionState.option.isCorrect}
                        on:click={() => onOptionClick(index)}>{optionState.option.text}</button>
                    {/each}
                </div>
            </div>
            {:else if showResume}
            <div class="resume">

                <div class="resume-box white-panel">
                    {#if isSuccess}
                        <img class="manzana-final" src="/images/manzana-win.svg" alt="">
                        <div class="win-text">
                            <strong>¡Genial</strong>, has respondido correctamente <strong>{wins} de {challenge.questions.length} preguntas!</strong>
                        </div>
                    {:else}
                        <img class="manzana-final" src="/images/manzana-fail.svg" alt="">
                        <div class="fail-text">
                            ¡Has respondido correctamente <strong>{wins} de {challenge.questions.length} preguntas, quizás debas seguir intentándolo!</strong>
                        </div>
                    {/if}
                    <button type="button" class="continue-btn" on:click={onContinue}>Continuar ruta</button>
                </div>
            </div>
            {/if}
        </div>
        <div class="face" slot="face-b">
            <div class="feedback white-panel" class:feedback-win={isCorrect} class:feedback-fail={!isCorrect}>

                <div class="indicator">
                    {#if isCorrect}
                    <img class="win-icon" src="/images/check.svg" alt="">
                    {:else}
                    <img class="fail-icon" src="/images/times-bold.svg" alt="">
                    {/if}
                </div>
    
                <div class="feedback-text">
                    <h3>{#if isCorrect}¡Muy bien!{:else}¡Mal!{/if}</h3>
                    {feedbackText}
                </div>

                <div class="feedback-btn-container">
                    <button class="next-btn" type="button" on:click={onNext}>Siguiente</button>
                </div>
            </div>
        </div>
    </Rotary>

</div>